import { gql, useLazyQuery } from '@apollo/client'
import { Box, Button, Stack, Typography } from '@mui/material'
import {
  AdCampaignData,
  Brand,
  CampaignChannel,
  Query,
  Location,
} from '@promoboxx/graphql-gateway-types'
import useFilter from '@promoboxx/use-filter'
import I18n from 'i18n-js'
import moment, { Moment, MomentInput } from 'moment'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'

import campaignContentPaths from '@src/components/content/campaignContentPaths'
import ContentFilters from '@src/components/content/ContentFilters'
import PlatformAvatarGroup from '@src/components/reporting/PlatformAvatarGroup'
import { campaignTotals } from '@src/components/reporting/statsTotals'
import useRetailerAccountWallet from '@src/components/wallet/useRetailerAccountWallet'
import useRouter from '@src/hooks/useRouter'
import { ThemeAwareColor } from '@src/lib/theme'
import urlParamStore from '@src/lib/urlParamStore'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'
import { useLocations } from '@src/lib/useLocations'
import useModalState from '@src/lib/useModalState'
import useOnRetailerAccountChange from '@src/lib/useOnRetailerAccountChange'
import usePreviouslySelectedBrand from '@src/lib/usePreviouslySelectedBrand'
import { campaignMinimumSpend, formatWithoutCents } from '@src/lib/Utils'
import { brandsById } from '@src/stores/app/selectors'

import {
  CampaignCardActionAreaDot,
  CampaignCardQuickStat,
  CampaignCardTruncatedDescription,
} from '../CampaignCard'
import CampaignResultsContainer from '../CampaignResultsContainer'
import { OmniCampaignCardProps } from '../OmniCampaignCard'
import RequestCampaignsDialog from '../RequestCampaignsDialog'

type NormalizedChannels = {
  campaignId: CampaignChannel['id']
  type: CampaignChannel['channel']
  channelCampaignId: CampaignChannel['channel_campaign_id']
  to: string
}

export const GET_PAID_CAMPAIGNS_SEARCH = gql`
  query GetPaidCampaignsSearchContentPage(
    $retailer_id: ID!
    $retailer_ids_array: [ID!]!
    $page_size: Int!
    $offset: Int!
  ) {
    getAdsByRetailer(
      params: {
        retailer_id: $retailer_id
        filter_paging_parameters: {
          page_size: $page_size
          offset: $offset
          filter_groups: [
            {
              filters: [
                { column: "state", value: "active", operator: "=" }
                { column: "state", value: "complete", operator: "=" }
                { column: "state", value: "published", operator: "=" }
              ]
            }
            {
              filters: [
                { column: "invite_state", value: "accepted", operator: "=" }
                { column: "invite_state", value: "failed", operator: "=" }
                { column: "invite_state", value: "pending", operator: "=" }
                { column: "invite_state", value: "invited", operator: "=" }
                { column: "invite_state", value: "expired", operator: "!=" }
              ]
            }
          ]
        }
        include_invitations: true
        include_campaign_channels: true
      }
    ) {
      data {
        id
        brand_id
        starts_at
        ends_at
        state
        objective
        title
        type
        thumbnail {
          source_id
          source_type
        }
        campaign_invitation {
          id
          state
          intended_amount_cents
        }
        campaign_channels {
          id
          campaign_id
          channel
          channel_campaign_id
          created_at
        }
        platforms
        automated

        getStats(params: { retailer_ids: $retailer_ids_array }) {
          stats {
            data {
              ads {
                actions {
                  post_reaction_like
                  post_reaction_haha
                  post_reaction_angry
                  post_reaction_sad
                  post_reaction_love
                  post_reaction_wow
                  clicks
                  video_views
                  comments
                  photo_views
                  posts
                  rsvps
                  retweets
                  favorites
                }
                clicks {
                  clicks
                  clicked
                }
                views {
                  impressions_organic
                  impressions_paid
                  opened
                }
              }
            }
          }
        }
      }
      paging {
        total_results
        page_size
      }
    }
  }
`

export interface NormalizedPaidAdCampaign extends Omit<AdCampaignData, 'type'> {
  isFeatured?: boolean
  locations: Location[] | null
  startDate: string
  endDate: string
  channels: NormalizedChannels[]
  description: string
  type: string
  thumbnailSrcId: string
  activateUrl: string
  isActivatable: boolean
  isActivated: boolean
  creditAmount: number
  brandName: string
  brandLogo: string
  onViewOrActivate: () => void
  cardImage: string
  paidType: AdCampaignData['type']
  brandUuid: NonNullable<Brand['uuid']>
}

type BrandsMap = {
  [x: string]: Brand
}

const PaidAdCampaigns: React.FC<
  React.PropsWithChildren<{
    brandFilter?: string
    filterBarRef: HTMLDivElement | null
  }>
> = ({ filterBarRef, brandFilter }) => {
  const pageSize = 24
  const { push } = useRouter()
  const { isOpen, toggleModal } = useModalState()
  const params = campaignContentPaths.index.useParams()
  const [campaignCards, setCampaignCards] = useState<OmniCampaignCardProps[]>(
    [],
  )

  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()
  const retailerAccount = ensureCurrentRetailerAccount

  const { locations } = useLocations(retailerAccount)
  const brandData = useSelector(brandsById) as BrandsMap
  // This query has paging data returned but does not accept paging params
  const [getPaidCampaignSearch, { data, called, refetch }] =
    useLazyQuery<Query>(GET_PAID_CAMPAIGNS_SEARCH)

  const defaultRetailerIds = retailerAccount.retailers
    ?.map((retailer) => {
      if (brandFilter === 'brand') {
        return !retailer.brand?.community ? retailer.id : ''
      } else if (brandFilter === 'industry') {
        return retailer.brand?.community ? retailer.id : ''
      }
      return retailer.id
    })
    .filter((id) => id)

  const { previouslySelectedRetailers } = usePreviouslySelectedBrand(
    undefined,
    brandFilter,
  )

  const { filterInfo, updateFilter, resetFilter, isLoading, setPage } =
    useFilter('content.paid_campaigns', {
      shouldForceRunOnMount: true,
      defaultFilterInfo: {
        filter: {
          brands: previouslySelectedRetailers.map((x) => x.id),
        },
      },
      onChange(filterInfo) {
        const retailerIds =
          filterInfo.filter.brands.includes('0') ||
          !filterInfo.filter.brands.length
            ? defaultRetailerIds
            : filterInfo.filter.brands

        const variables = {
          offset: filterInfo.offset,
          page_size: pageSize,
          retailer_id: retailerIds?.join(','),
          retailer_ids_array: retailerIds,
        }
        if (filterInfo.filter.brands && filterInfo.filter.brands.length) {
          variables.retailer_id = filterInfo.filter.brands.join(',')
        }

        return (
          called ? refetch(variables) : getPaidCampaignSearch({ variables })
        )
          .then((response) => {
            return {
              filterInfo: {
                totalResults:
                  response?.data?.getAdsByRetailer?.paging?.total_results || 0,
              },
            }
          })
          .catch((error) => {
            console.error(error)
          })
      },
      store: urlParamStore,
    })

  const mergeWithoutDuplicates = (
    prev: OmniCampaignCardProps[],
    newResults: OmniCampaignCardProps[],
  ) => {
    const existingIds = new Set(prev.map((campaign) => campaign.id))
    const nonDuplicateResults = newResults.filter(
      (campaign) => !existingIds.has(campaign.id),
    )
    return [...prev, ...nonDuplicateResults]
  }

  const { findLedgersApplicableToPaidAd } = useRetailerAccountWallet()

  useEffect(() => {
    if (!isLoading && data?.getAdsByRetailer?.data) {
      // Normalize the data we get back into results
      const sortedResults = [...data.getAdsByRetailer.data]

      sortedResults.sort((a, b) => {
        return new Date(b.starts_at).valueOf() - new Date(a.starts_at).valueOf()
      })

      const results = sortedResults.map((paidAd) => {
        const brand = brandData[paidAd?.brand_id]
        const now = new Date()
        const activateUrl = campaignContentPaths.activatePaidCampaign.build({
          retailerAccountSlug: params.retailerAccountSlug,
          activeTab: 'paid',
          campaignId: paidAd?.id,
        })

        const channels: Array<NormalizedChannels> = []
        paidAd.campaign_channels.forEach((campaignChannel) => {
          channels.push({
            campaignId: campaignChannel.campaign_id,
            type: campaignChannel.channel,
            channelCampaignId: campaignChannel.channel_campaign_id,
            to: activateUrl,
          })
          if (campaignChannel.channel === 'facebook') {
            channels.push({
              campaignId: campaignChannel.campaign_id,
              type: 'instagram',
              channelCampaignId: campaignChannel.channel_campaign_id,
              to: activateUrl,
            })
          }
        })

        const creditAmount =
          paidAd.type === 'retailer_funded'
            ? campaignMinimumSpend(
                new Date(paidAd.starts_at),
                new Date(paidAd.ends_at || ''),
              ) * 100
            : paidAd?.campaign_invitation
              ? Number(paidAd?.campaign_invitation.intended_amount_cents)
              : 0

        const paidAdColor: ThemeAwareColor =
          paidAd.type === 'retailer_funded' ? 'secondary' : 'premiumAds'

        //This is activated, but has failed onthe backend along the way. We need Backend developer intervention.
        const isActivated = ['accepted', 'failed', 'pending'].includes(
          String(paidAd?.campaign_invitation?.state),
        )

        let quickStats: React.ReactNode = undefined

        if (paidAd.campaign_invitation?.state === 'accepted') {
          const { totals } = campaignTotals(
            paidAd.getStats?.stats?.[0]?.data?.ads,
          )

          quickStats = (
            <Stack direction="row" justifyContent="space-around">
              <CampaignCardQuickStat
                number={totals.views}
                label={I18n.t('shared.views')}
              />
              <CampaignCardQuickStat
                number={totals.actions}
                label={I18n.t('shared.actions')}
              />
              <CampaignCardQuickStat
                number={totals.clicks}
                label={I18n.t('shared.clicks')}
              />
            </Stack>
          )
        }

        const omniCampaignCard: OmniCampaignCardProps = {
          id: paidAd.id,
          title: paidAd.title || '',
          subheader: displayPremiumDate(
            now,
            moment(paidAd.starts_at),
            moment(paidAd.ends_at),
          ),

          description: (
            <>
              <CampaignCardTruncatedDescription>
                {paidAd.objective}
              </CampaignCardTruncatedDescription>

              {quickStats}

              {paidAd.type === 'retailer_funded' ? (
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={(theme) => ({
                    position: 'absolute',
                    right: theme.spacing(2),
                    bottom: theme.spacing(2),
                  })}
                >
                  {I18n.t('content.availableFundsWithAmount', {
                    amount: formatWithoutCents.format(
                      findLedgersApplicableToPaidAd(brand?.uuid || '', {
                        excludeExpired: true,
                        excludeEmpty: true,
                      }).reduce((memo, x) => {
                        return memo + Number(x.balance)
                      }, 0) / 100,
                    ),
                  })}
                </Typography>
              ) : undefined}
            </>
          ),

          href: activateUrl,
          // isLegacyLink: true,

          actionAreaLabel: (
            <>
              <CampaignCardActionAreaDot />

              <span>
                {isActivated
                  ? I18n.t('content.viewPremiumCampaign')
                  : I18n.t('content.claimAdFunds')}
              </span>
            </>
          ),
          actionAreaColor: 'pbxxCyan',
          actionAreaInvert: isActivated,

          // sashLabel: isNew ? 'New' : '',
          // sashColor: 'discovery',
          // sashTooltip: 'Hello World',

          mediaPublicId: paidAd?.thumbnail?.source_id,
          brandLogo: brand?.logo_url,

          secondaryContent: (
            <>
              <Stack direction="row" spacing={2} alignItems="center">
                <PlatformAvatarGroup platforms={paidAd.platforms} />

                <Box flexGrow={1} textAlign="right">
                  {paidAd.type === 'retailer_funded'
                    ? I18n.t('content.minimumSpend')
                    : undefined}

                  {paidAd.type !== 'retailer_funded' && !isActivated
                    ? I18n.t('content.claimableAdFunds')
                    : undefined}
                </Box>

                <div>
                  <Box
                    bgcolor={`${paidAdColor}.main`}
                    color={`${paidAdColor}.contrastText`}
                    paddingX={2}
                    paddingY={0.5}
                  >
                    {formatWithoutCents.format(creditAmount / 100)}
                  </Box>
                </div>
              </Stack>
            </>
          ),
        }

        return omniCampaignCard
      })

      setCampaignCards((prev) => mergeWithoutDuplicates(prev, results))
    }
  }, [
    isLoading,
    data,
    retailerAccount,
    brandData,
    locations,
    params,
    push,
    findLedgersApplicableToPaidAd,
  ])

  useOnRetailerAccountChange(() => {
    resetFilter()
    setCampaignCards([])
  })

  return filterBarRef ? (
    <>
      {createPortal(
        <ContentFilters
          brandFilter={brandFilter}
          filterInfo={filterInfo}
          onClear={() => {
            resetFilter()
            setCampaignCards([])
          }}
          hideFilters={['search', 'tags', 'show', 'filterByChannel']}
          onChange={(key, value) => {
            updateFilter({ [key]: value })
            setCampaignCards([])
          }}
        />,
        filterBarRef,
        'paid-search-filter-bar',
      )}

      <CampaignResultsContainer
        pageSize={filterInfo.pageSize}
        isLoading={isLoading}
        results={campaignCards}
        showMoreHandler={() => {
          setPage(filterInfo.page + 1)
        }}
        resetFilters={() => {
          resetFilter()
          setCampaignCards([])
        }}
        totalResults={filterInfo.totalResults}
        noResultsText={I18n.t('content.noPaidAdsFound')}
      >
        {!isLoading &&
          (filterInfo.totalResults === campaignCards.length ||
            campaignCards.length === 0) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Button
                color="secondary"
                onClick={toggleModal}
                size="large"
                variant="outlined"
              >
                {I18n.t('content.requestMore')}
              </Button>
            </Box>
          )}
      </CampaignResultsContainer>

      <RequestCampaignsDialog
        onClose={toggleModal}
        open={isOpen}
        total={filterInfo.totalResults}
        preFill={{
          desiredContentTypes: ['Paid'],
        }}
      />
    </>
  ) : null
}

const displayPremiumDate = (
  now: MomentInput,
  startDate: Moment,
  endDate: Moment,
) => {
  if (startDate.isAfter(now)) {
    return I18n.t('content.beginsOnDate', {
      date: startDate.format('L, h:mma'),
    })
  } else if (endDate.isBefore(now)) {
    return I18n.t('content.campaignEnded')
  } else {
    return I18n.t('content.endsOnDate', {
      date: endDate.format('L, h:mma'),
    })
  }
}

export default PaidAdCampaigns
